import * as LabelPrimitive from "@radix-ui/react-label";
import type {
  InputHTMLAttributes,
  LabelHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

import { cn } from "../../lib/cn";

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <LabelPrimitive.Root className={cn("text-sm font-medium leading-none", className)} {...props} />
  );
}

export const inputClassName =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-destructive/30";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(inputClassName, className)} {...props} />;
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

/** Label + input + (hint | error), wired for screen readers; works with react-hook-form's register. */
export function Field({ label, error, hint, id, className, ...input }: FieldProps) {
  const inputId = id ?? input.name ?? label;
  const errorId = `${inputId}-error`;
  return (
    <div className={cn("mb-4 flex flex-col gap-1.5", className)}>
      <Label htmlFor={inputId}>{label}</Label>
      <Input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...input}
      />
      {hint && !error ? <small className="text-muted-foreground text-xs">{hint}</small> : null}
      {error ? (
        <small id={errorId} className="text-destructive text-xs">
          {error}
        </small>
      ) : null}
    </div>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
}

/** The multi-line sibling of Field, with the same label, hint and error wiring. */
export function TextAreaField({ label, error, hint, id, className, ...area }: TextAreaFieldProps) {
  const areaId = id ?? area.name ?? label;
  const errorId = `${areaId}-error`;
  return (
    <div className={cn("mb-4 flex flex-col gap-1.5", className)}>
      <Label htmlFor={areaId}>{label}</Label>
      <textarea
        id={areaId}
        rows={4}
        className={cn(inputClassName, "h-auto min-h-20 py-2")}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        {...area}
      />
      {hint && !error ? <small className="text-muted-foreground text-xs">{hint}</small> : null}
      {error ? (
        <small id={errorId} className="text-destructive text-xs">
          {error}
        </small>
      ) : null}
    </div>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly label: string;
  readonly options: readonly { value: string; label: string }[];
}

/** A native select on purpose: keyboard, screen readers and test tooling all know it. */
export function SelectField({ label, options, id, className, ...select }: SelectFieldProps) {
  const selectId = id ?? select.name ?? label;
  return (
    <div className={cn("mb-4 flex flex-col gap-1.5", className)}>
      <Label htmlFor={selectId}>{label}</Label>
      <select id={selectId} className={cn(inputClassName, "pr-8")} {...select}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Checkbox({
  label,
  id,
  className,
  ...input
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  const inputId = id ?? input.name ?? label;
  return (
    <div className={cn("mb-4 flex items-center gap-2", className)}>
      <input
        id={inputId}
        type="checkbox"
        className="size-4 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        {...input}
      />
      <Label htmlFor={inputId}>{label}</Label>
    </div>
  );
}
